(() => {
const LIMITS = Object.freeze({
  dailyKwh: [0.1, 30],
  sunHours: [0.5, 8],
  reserveDays: [0.5, 7],
  panelWatts: [50, 600],
});

const ASSUMPTIONS = Object.freeze({
  solarYield: 0.75,
  inverterEfficiency: 0.90,
  batteryUsableFraction: 0.80,
  controllerHeadroom: 1.25,
});

const CHARGE_VOLTAGES = Object.freeze({ 12: 12.8, 24: 25.2, 36: 38.4, 48: 51.2 });
const ALLOWED_VOLTAGES = new Set([12, 24, 36, 48]);
const ALLOWED_LOADS = new Set(['ac', 'dc']);

function calculateSolar(input) {
  for (const [key, [min, max]] of Object.entries(LIMITS)) {
    const value = input[key];
    if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
      throw new RangeError(`${key} must be between ${min} and ${max}`);
    }
  }
  if (!ALLOWED_VOLTAGES.has(input.voltage)) throw new RangeError('Unsupported nominal system voltage');
  if (!ALLOWED_LOADS.has(input.loadType)) throw new RangeError('Unsupported load type');

  const { dailyKwh, voltage, sunHours, reserveDays, panelWatts, loadType } = input;
  const dailyWh = dailyKwh * 1000;
  const loadEfficiency = loadType === 'ac' ? ASSUMPTIONS.inverterEfficiency : 1;
  const batteryDailyWh = dailyWh / loadEfficiency;
  const minimumSolarWatts = batteryDailyWh / (sunHours * ASSUMPTIONS.solarYield);
  const panelCount = Math.ceil(minimumSolarWatts / panelWatts);
  const arrayWatts = panelCount * panelWatts;
  const requiredBatteryWh = batteryDailyWh * reserveDays / ASSUMPTIONS.batteryUsableFraction;
  const requiredBatteryAh = requiredBatteryWh / CHARGE_VOLTAGES[voltage];
  const suggestedBatteryAh = Math.ceil((requiredBatteryAh - 1e-9) / 10) * 10;
  const suggestedControllerAmps = Math.ceil((arrayWatts / voltage * ASSUMPTIONS.controllerHeadroom - 1e-9) / 10) * 10;
  const estimatedDailyOutputWh = arrayWatts * sunHours * ASSUMPTIONS.solarYield * loadEfficiency;

  return {
    dailyWh, batteryDailyWh, loadEfficiency,
    minimumSolarWatts, panelCount, arrayWatts,
    requiredBatteryWh, requiredBatteryAh, suggestedBatteryAh,
    suggestedControllerAmps, estimatedDailyOutputWh,
    outputMarginWh: estimatedDailyOutputWh - dailyWh,
  };
}

function positive(name, value, min, max) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
    throw new RangeError(`${name} must be between ${min} and ${max}`);
  }
}

// Table 1: ideal panel production, before operating losses.
function panelEnergy({ watts, sunHours }) {
  positive('watts', watts, 1, 30000);
  positive('sunHours', sunHours, 0.5, 8);
  return watts * sunHours;
}

// Tables 2 and 3 use the same battery calculation voltages as Table 4.

// Table 2: replace 80% of the battery energy at the selected calculation voltage.
function batteryRechargeSolar({ batteryAh, voltage, sunHours, rechargeDays }) {
  positive('batteryAh', batteryAh, 1, 10000);
  if (!Object.hasOwn(CHARGE_VOLTAGES, voltage)) throw new RangeError('Unsupported battery voltage');
  positive('sunHours', sunHours, 0.5, 8);
  positive('rechargeDays', rechargeDays, 0.5, 30);
  return batteryAh * CHARGE_VOLTAGES[voltage] * 0.8 / sunHours / rechargeDays;
}

// Table 3: the worksheet's estimated solar charging current uses 80% of rated power.
function estimatedSolarCurrent({ solarWatts, chargeSystemVoltage }) {
  positive('solarWatts', solarWatts, 1, 30000);
  if (!Object.hasOwn(CHARGE_VOLTAGES, chargeSystemVoltage)) throw new RangeError('Unsupported charging system voltage');
  return solarWatts * 0.8 / CHARGE_VOLTAGES[chargeSystemVoltage];
}

function constantCurrentHours(input) {
  positive('capacityWh', input.capacityWh, 1, 500000);
  const currentA = estimatedSolarCurrent(input);
  return input.capacityWh / (currentA * CHARGE_VOLTAGES[input.chargeSystemVoltage]);
}

// Table 4: keep the worksheet's 90% solar yield and correct autonomy days to a multiplier.
function offGridQuickEstimate({ loadWatts, runHours, voltage, sunHours, reserveDays }) {
  positive('loadWatts', loadWatts, 1, 30000);
  positive('runHours', runHours, 0.1, 24);
  positive('sunHours', sunHours, 0.5, 8);
  positive('reserveDays', reserveDays, 0.5, 7);
  if (!ALLOWED_VOLTAGES.has(voltage)) throw new RangeError('Unsupported voltage');
  const dailyWh = loadWatts * runHours;
  return { dailyWh, batteryAh: dailyWh * reserveDays / CHARGE_VOLTAGES[voltage] / 0.8, solarWatts: dailyWh / sunHours / 0.9 };
}

const root = document.getElementById('acopower-solar-calculator');
if (!root) return;

const form = document.getElementById('solar-form');
const errorBox = document.getElementById('error-box');
const inputError = document.getElementById('input-error');
const results = document.getElementById('results-content');
const quickResults = document.getElementById('quick-results');
const numberFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const chargeFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const wholeFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const put = (id, value) => { document.getElementById(id).textContent = value; };
const fmt = (value) => numberFormat.format(value);
const chargeFmt = (value) => chargeFormat.format(value);
const whole = (value) => wholeFormat.format(value);

function readValue(name) {
  const control = form.elements.namedItem(name);
  if (control.value.trim() === '') return NaN;
  return Number(control.value);
}

function render() {
  for (const control of form.querySelectorAll('input[aria-invalid]')) control.removeAttribute('aria-invalid');
  const inputs = {
    loadWatts: readValue('loadWatts'),
    runHours: readValue('runHours'),
    voltage: readValue('voltage'),
    sunHours: readValue('sunHours'),
    reserveDays: readValue('reserveDays'),
    panelWatts: readValue('panelWatts'),
    loadType: form.elements.namedItem('loadType').value,
  };
  const showError = (message, fields = [], hideQuick = true) => {
    for (const field of fields) form.elements.namedItem(field).setAttribute('aria-invalid', 'true');
    errorBox.textContent = message;
    inputError.textContent = message;
    errorBox.hidden = false;
    inputError.hidden = false;
    results.hidden = true;
    quickResults.hidden = hideQuick;
  };
  const ranges = { loadWatts: [1, 30000], runHours: [0.1, 24], sunHours: LIMITS.sunHours, reserveDays: LIMITS.reserveDays };
  const labels = { loadWatts: 'load power', runHours: 'run time', sunHours: 'peak sun hours', reserveDays: 'autonomy' };
  for (const [name, [min, max]] of Object.entries(ranges)) {
    if (!Number.isFinite(inputs[name]) || inputs[name] < min || inputs[name] > max) {
      showError(`Enter a value for ${labels[name]} between ${min} and ${max}.`, [name]);
      return;
    }
  }
  let quick;
  try {
    quick = offGridQuickEstimate(inputs);
    put('dailyEnergy', `${fmt(quick.dailyWh / 1000)} kWh / day`);
    put('quickVoltage', CHARGE_VOLTAGES[inputs.voltage]);
    put('quickBatteryAh', `${fmt(quick.batteryAh)} Ah`);
    put('quickSolarWatts', `${fmt(quick.solarWatts)} W`);
    quickResults.hidden = false;
  } catch {
    showError('Invalid worksheet inputs. Check each value and try again.');
    return;
  }
  const dailyKwh = inputs.loadWatts * inputs.runHours / 1000;
  if (dailyKwh < LIMITS.dailyKwh[0] || dailyKwh > LIMITS.dailyKwh[1]) {
    showError('For the recommended configuration, daily energy (load power × run time) must be between 0.1 and 30 kWh.', ['loadWatts', 'runHours'], false);
    return;
  }
  const [panelMin, panelMax] = LIMITS.panelWatts;
  if (!Number.isFinite(inputs.panelWatts) || inputs.panelWatts < panelMin || inputs.panelWatts > panelMax) {
    showError(`Enter a value for power per panel between ${panelMin} and ${panelMax}.`, ['panelWatts'], false);
    return;
  }
  try {
    const input = { ...inputs, dailyKwh };
    const r = calculateSolar(input);
    errorBox.hidden = true;
    inputError.hidden = true;
    results.hidden = false;
    quickResults.hidden = false;
    put('arrayWatts', whole(r.arrayWatts));
    put('panelCombination', `${whole(r.panelCount)} panels × ${whole(input.panelWatts)} W`);
    put('minimumSolarWatts', `≥ ${whole(Math.ceil(r.minimumSolarWatts))} W`);
    put('batteryAh', whole(r.suggestedBatteryAh));
    put('batteryKwh', `Calculated minimum ${fmt(r.requiredBatteryWh / 1000)} kWh · ${fmt(r.requiredBatteryAh)} Ah at ${CHARGE_VOLTAGES[input.voltage]} V`);
    put('controllerAmps', whole(r.suggestedControllerAmps));
    put('estimatedOutput', `${fmt(r.estimatedDailyOutputWh / 1000)} kWh / day`);
    put('yieldNote', `About ${fmt(r.outputMarginWh / 1000)} kWh above daily demand at the entered sun hours`);
    document.getElementById('yieldFill').style.width = `${Math.min(100, r.dailyWh / r.estimatedDailyOutputWh * 100)}%`;
    const warning = document.getElementById('scale-warning');
    const large = r.panelCount > 24 || r.suggestedControllerAmps > 200;
    warning.hidden = !large;
    if (large) warning.textContent = 'This is a large system. Have a qualified installer review system voltage, controller count, and circuit design.';
  } catch {
    showError('Invalid configuration inputs. Check each value and try again.', [], false);
  }
}
form.addEventListener('input', render);
form.addEventListener('change', render);
form.addEventListener('reset', () => queueMicrotask(render));
render();

for (const tool of root.querySelectorAll('.asc-sheet-tool')) {
  const values = () => Object.fromEntries([...tool.querySelectorAll('input,select')].map(el => [el.name, el.value.trim() === '' ? NaN : Number(el.value)]));
  const clear = () => { for (const el of tool.querySelectorAll('[data-result],[data-current]')) el.textContent = '—'; };
  const refresh = () => {
    const error = tool.querySelector('.asc-tool-error');
    try {
      const input = values();
      switch (tool.dataset.tool) {
        case 'battery':
          tool.querySelector('[data-battery-voltage]').textContent = CHARGE_VOLTAGES[input.voltage] ?? '—';
          tool.querySelector('[data-result]').textContent = `${fmt(batteryRechargeSolar(input))} W`;
          break;
        case 'charging':
          tool.querySelector('[data-current]').textContent = `${chargeFmt(estimatedSolarCurrent(input))} A`;
          tool.querySelector('[data-result]').textContent = `${chargeFmt(constantCurrentHours(input))} hours`;
          tool.querySelector('[data-charge-voltage]').textContent = CHARGE_VOLTAGES[input.chargeSystemVoltage];
          break;
      }
      error.hidden = true;
    } catch {
      clear();
      error.textContent = 'Enter values within the ranges shown. Fields cannot be blank or zero.';
      error.hidden = false;
    }
  };
  tool.addEventListener('input', refresh);
  tool.addEventListener('change', refresh);
  tool.addEventListener('submit', event => event.preventDefault());
  refresh();
}


})();
